/**
 * useXpToast Hook
 *
 * Returns the recent XP gain events for toast display.
 * Each toast auto-clears after 3 seconds.
 *
 * @module hooks/useXpToast
 * @see stores/xpStore
 */

import { useEffect, useRef } from 'react';
import { useXpStore } from '@/stores/xpStore';
import type { RecentXpGain } from '@/stores/xpStore';

export interface UseXpToastReturn {
  /** All active toast items (max 3) */
  toasts: RecentXpGain[];
  /** @deprecated Use toasts instead */
  recentXpGain: RecentXpGain | null;
  /** Dismiss a specific toast by id */
  dismiss: (id: string) => void;
  /** Dismiss all toasts */
  dismissAll: () => void;
}

const TOAST_DURATION_MS = 3000;

/**
 * Hook for XP gain toast notifications with stacking support.
 *
 * - Returns up to 3 active toast items
 * - Each toast auto-clears after 3 seconds
 * - Provides dismiss for manual clearing
 */
export function useXpToast(): UseXpToastReturn {
  const toasts = useXpStore((s) => s.recentXpGains);
  const dismissXpGain = useXpStore((s) => s.dismissXpGain);
  const clearRecentXpGain = useXpStore((s) => s.clearRecentXpGain);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Set up auto-dismiss timers for each toast
  useEffect(() => {
    const currentTimers = timersRef.current;

    for (const toast of toasts) {
      if (!currentTimers.has(toast.id)) {
        const timer = setTimeout(() => {
          dismissXpGain(toast.id);
          currentTimers.delete(toast.id);
        }, TOAST_DURATION_MS);
        currentTimers.set(toast.id, timer);
      }
    }

    // Clean up timers for toasts that no longer exist
    for (const [id, timer] of currentTimers) {
      if (!toasts.some((t) => t.id === id)) {
        clearTimeout(timer);
        currentTimers.delete(id);
      }
    }

    return () => {
      for (const timer of currentTimers.values()) {
        clearTimeout(timer);
      }
    };
  }, [toasts, dismissXpGain]);

  return {
    toasts,
    recentXpGain: toasts[toasts.length - 1] ?? null,
    dismiss: (id: string) => {
      const timer = timersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
      dismissXpGain(id);
    },
    dismissAll: clearRecentXpGain,
  };
}
